import type { JourneyAction } from './steps.ts'
import { isVariableName } from './template.ts'
import { templateRefs } from './template-fields.ts'

// Declarations use bare names (`code`); consumers reference them as `$code`.
export function canonicalJourneyVariableName(rawName: string): string | null {
  const name = rawName.startsWith('$') ? rawName.slice(1) : rawName
  return isVariableName(name) ? name : null
}

export function setJourneyVariable(vars: Map<string, string>, rawName: string, value: string): string | null {
  const name = canonicalJourneyVariableName(rawName)
  if (!name) return null
  vars.set(name, value)
  return name
}

export function normalizeStoredJourneyVariables<
  TAction extends { variable?: string },
  TVariable extends { name: string },
>(actions: TAction[], variables: TVariable[]): { actions: TAction[]; variables: TVariable[] } {
  return {
    actions: actions.map(action => {
      if (!action.variable) return action
      const variable = canonicalJourneyVariableName(action.variable)
      return variable && variable !== action.variable ? { ...action, variable } : action
    }),
    variables: variables.map(variableDefinition => {
      const name = canonicalJourneyVariableName(variableDefinition.name)
      return name && name !== variableDefinition.name ? { ...variableDefinition, name } : variableDefinition
    }),
  }
}

export function extractReferencedVarNames(actions: JourneyAction[]): Set<string> {
  return new Set(templateRefs(actions, 'all'))
}

// The key a journey step's STORE TARGET (its `variable` field: awaitEmail's set_variable, read_variable, a
// drag-preview capture) must write under. References read those values with the `$name` sigil syntax
// the template renderer resolves — its lexer captures the BARE name after the `$` — and initializeVariables /
// extractReferencedVarNames key that same bare name. A store target authored WITH the sigil (`variable:
// "$code"` instead of `"code"`) would otherwise be written under the literal key "$code", which the renderer
// can NEVER read (its match begins after the sigil), so the value is silently unreachable and every `$code`
// reference resolves to the placeholder initializeVariables fabricated for the still-unset bare name. Drop a
// single leading `$` so the store lands in the same key-space the reference side reads; a bare name is
// unchanged. (Bug Report #9698: an awaitEmail step authored `variable: "$code"` stored the emailed OTP under
// "$code" while the "type the code" step's `$code` read the fabricated "test-xxxxxx" placeholder, so the run
// typed garbage the field correctly rejected and then filed a false "the code field cannot accept input" bug.)
export function variableStoreKey(name: string): string {
  return name.startsWith('$') ? name.slice(1) : name
}
